import React from 'react';
import {
    TableRow,
    TableCell,
    Checkbox,
    IconButton,
    Typography,
    Chip,
    Box,
    Switch,
    Tooltip,
} from '@mui/material';
import { MoreVert as MoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';

import { LAST_STATUS_META } from '../webhookConstants';

dayjs.extend(relativeTime);

const WebhooksTableRow = ({
    webhook,
    index,
    isSelected,
    onRowCheckboxClick,
    onMenuOpen,
    onToggleActive,
    isToggling,
}) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const eventsCount = Array.isArray(webhook.events) ? webhook.events.length : 0;
    const statusMeta = webhook.last_status ? LAST_STATUS_META[webhook.last_status] : null;

    const lastFiredLabel = webhook.last_fired_at
        ? dayjs(webhook.last_fired_at).locale(isArabic ? 'ar' : 'en').fromNow()
        : (isArabic ? 'لم يُشغّل بعد' : 'Never');

    return (
        <TableRow hover selected={isSelected}>
            <TableCell padding="checkbox">
                <Checkbox
                    checked={isSelected}
                    onClick={(event) => onRowCheckboxClick(event, webhook.id)}
                />
            </TableCell>

            {/* S/N */}
            <TableCell align="start">{index + 1}</TableCell>

            {/* Name */}
            <TableCell>
                <Typography variant="body2" fontWeight="bold" sx={{ color: 'primary.main' }}>
                    {webhook.name}
                </Typography>
            </TableCell>

            {/* URL */}
            <TableCell>
                <Tooltip title={webhook.url || ''} placement="top" arrow>
                    <Box sx={{ maxWidth: 320 }}>
                        <Typography
                            variant="body2"
                            noWrap
                            sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}
                        >
                            {webhook.url}
                        </Typography>
                    </Box>
                </Tooltip>
            </TableCell>

            {/* Events count */}
            <TableCell align="center">
                <Chip
                    label={eventsCount}
                    size="small"
                    color="info"
                    variant="tonal"
                    sx={{ fontWeight: 'bold', borderRadius: '6px', minWidth: 40 }}
                />
            </TableCell>

            {/* Active toggle */}
            <TableCell align="center">
                <Switch
                    size="small"
                    checked={Boolean(webhook.is_active)}
                    onChange={(e) => {
                        e.stopPropagation();
                        onToggleActive?.(webhook, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isToggling}
                />
            </TableCell>

            {/* Last status */}
            <TableCell align="center">
                {statusMeta ? (
                    <Chip
                        label={isArabic ? statusMeta.labelAr : statusMeta.labelEn}
                        size="small"
                        color={statusMeta.color}
                        variant="tonal"
                        sx={{ fontWeight: 'bold', borderRadius: '6px', minWidth: 80 }}
                    />
                ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                )}
            </TableCell>

            {/* Last fired (relative) */}
            <TableCell align="center">
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                    {lastFiredLabel}
                </Typography>
            </TableCell>

            {/* Actions */}
            <TableCell align="center">
                <IconButton size="small" onClick={(e) => onMenuOpen(e, webhook)}>
                    <MoreIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default WebhooksTableRow;
